import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@agms/ui'
import { useLead, useUpdateLead, useDeleteLead, useTransitionLeadStage, manualTransitionTargets } from '../../../hooks/useLeads'
import { usePermissions } from '../../../hooks/useAuth'
import { apiErrorMessage, apiValidationErrors } from '../../../hooks/useErrorHandler'
import { LeadDetailContent } from './LeadDetailContent'

interface LeadAccountWorkspaceProps {
  leadId: string | undefined
  showHeader?: boolean
  showSharedSections?: boolean
}

export function LeadAccountWorkspace({
  leadId,
  showHeader = true,
  showSharedSections = true,
}: LeadAccountWorkspaceProps) {
  const navigate = useNavigate()
  const { canUpdateLeads, canDeleteLeads, canTransitionStage, canAssignLeads, canCreateTickets } = usePermissions()

  const { data: lead, isLoading, error } = useLead(leadId)
  const updateLead = useUpdateLead()
  const deleteLead = useDeleteLead()
  const transitionStage = useTransitionLeadStage()

  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showTransitionModal, setShowTransitionModal] = useState(false)
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false)
  const [transitionNote, setTransitionNote] = useState('')
  const [selectedStage, setSelectedStage] = useState('')
  const [transitionError, setTransitionError] = useState<string | null>(null)

  // Format date for display
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!lead) return

    const formData = new FormData(e.currentTarget)
    const data: Record<string, unknown> = {}

    // Extract form values
    const fields = [
      'dba_name', 'legal_name', 'contact_name', 'contact_phone', 'business_email',
      'website', 'business_description', 'ownership_type', 'lead_source'
    ]
    fields.forEach(field => {
      const value = formData.get(field)
      if (value !== null) data[field] = value || null
    })

    // Numeric fields
    const numericFields = ['years_in_business', 'monthly_volume', 'avg_ticket', 'high_ticket', 'pct_card_swiped', 'pct_ecommerce', 'pct_cnp']
    numericFields.forEach(field => {
      const value = formData.get(field)
      if (value) data[field] = parseFloat(value as string)
      else data[field] = null
    })

    // Boolean fields
    data.has_tax_id = formData.get('has_tax_id') === 'on'
    data.has_checking_account = formData.get('has_checking_account') === 'on'

    updateLead.mutate({ id: lead.id, data }, {
      onSuccess: () => setIsEditing(false)
    })
  }

  // Handle delete
  const handleDelete = () => {
    if (!lead) return
    deleteLead.mutate(lead.id, {
      onSuccess: () => navigate('/leads')
    })
  }

  // Inline assignee auto-save. Both slots route through the same
  // update endpoint; the backend enforces the `assign` policy. We fire
  // ONE partial update per change — react-query refresh handles the
  // re-render.
  const handleAssigneeChange = (
    slot: 'assigned_to' | 'assigned_to_secondary',
    userId: string | null,
  ) => {
    if (!lead) return
    updateLead.mutate({ id: lead.id, data: { [slot]: userId } })
  }

  // Open the existing Change Stage modal. Shared click target between
  // the header button and the (now clickable) PipelineBadge.
  const openStageModal = () => {
    if (!canTransitionStage()) return
    if (manualTransitionTargets(lead?.stage ?? '').length === 0) return
    setTransitionError(null)
    setShowTransitionModal(true)
  }

  // Handle stage transition
  const handleTransition = () => {
    if (!lead || !selectedStage) return
    setTransitionError(null)
    transitionStage.mutate({
      id: lead.id,
      data: { stage: selectedStage, note: transitionNote || undefined }
    }, {
      onSuccess: () => {
        setShowTransitionModal(false)
        setTransitionNote('')
        setSelectedStage('')
        setTransitionError(null)
      },
      onError: (error) => {
        const msg = apiErrorMessage(error, apiValidationErrors(error)?.stage?.[0] ?? 'Transition failed. Check the server logs.')
        setTransitionError(msg)
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading lead...</div>
      </div>
    )
  }

  if (error || !lead) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-4">Lead not found or access denied.</div>
            <Link to="/leads">
              <Button variant="secondary">Back to Leads</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <LeadDetailContent
      lead={lead}
      navigate={navigate}
      canUpdateLeads={canUpdateLeads}
      canDeleteLeads={canDeleteLeads}
      canTransitionStage={canTransitionStage}
      canAssignLeads={canAssignLeads}
      canCreateTickets={canCreateTickets}
      updateLead={updateLead}
      deleteLead={deleteLead}
      transitionStage={transitionStage}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      showDeleteConfirm={showDeleteConfirm}
      setShowDeleteConfirm={setShowDeleteConfirm}
      showTransitionModal={showTransitionModal}
      setShowTransitionModal={setShowTransitionModal}
      showCreateTicketModal={showCreateTicketModal}
      setShowCreateTicketModal={setShowCreateTicketModal}
      transitionNote={transitionNote}
      setTransitionNote={setTransitionNote}
      selectedStage={selectedStage}
      setSelectedStage={setSelectedStage}
      transitionError={transitionError}
      handleSubmit={handleSubmit}
      handleDelete={handleDelete}
      handleAssigneeChange={handleAssigneeChange}
      openStageModal={openStageModal}
      handleTransition={handleTransition}
      formatDate={formatDate}
      showHeader={showHeader}
      showSharedSections={showSharedSections}
    />
  )
}

